/**
 * Mode jour / mode nuit.
 *
 * Même mécanisme que compagnies.org, pour que les deux sites se comportent
 * pareil : un attribut `data-theme` sur `<html>`, une palette de jetons CSS par
 * thème, et `color-scheme` posé pour que les contrôles natifs — ascenseurs,
 * sélecteurs, champs de date — suivent.
 *
 * Le choix est mémorisé. Tant que l'utilisateur n'a rien choisi, la préférence
 * du système fait foi, et le site suit ses changements en direct.
 *
 * La politique de sécurité de contenu interdit tout script en ligne : le thème
 * ne peut donc pas être posé avant le premier rendu. La feuille de style teinte
 * le canevas sous `prefers-color-scheme` pour qu'aucun éclair blanc
 * n'apparaisse en attendant.
 */

export type Theme = 'light' | 'dark';

const CLE = 'budget-scc.theme';

/** Le choix mémorisé, ou `null` si l'utilisateur n'a rien choisi. */
export function themeChoisi(): Theme | null {
  try {
    const v = localStorage.getItem(CLE);
    return v === 'light' || v === 'dark' ? v : null;
  } catch {
    // Stockage refusé — navigation privée, réglage strict. On suit le système.
    return null;
  }
}

/** La préférence du système. */
export function themeSysteme(): Theme {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/** Le thème effectivement appliqué. */
export function themeActif(): Theme {
  return (document.documentElement.dataset['theme'] as Theme | undefined) ?? themeSysteme();
}

/** Pose le thème sur le document, sans le mémoriser. */
function poser(theme: Theme): void {
  document.documentElement.dataset['theme'] = theme;
  // compagnies.org porte les deux attributs : on fait de même, pour qu'une
  // feuille de style partagée entre les deux sites fonctionne sans adaptation.
  document.documentElement.dataset['modeNuit'] = theme === 'dark' ? 'on' : 'off';
}

/**
 * Applique le thème au chargement et suit le système tant que rien n'a été
 * choisi. `surChangement` est rappelé quand le thème change : les graphiques
 * lisent leurs couleurs au moment du rendu et doivent être redessinés.
 */
export function initialiserTheme(surChangement: (t: Theme) => void): void {
  poser(themeChoisi() ?? themeSysteme());

  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
    // Un choix explicite prime sur le système : on ne l'écrase pas.
    if (themeChoisi() !== null) return;
    poser(e.matches ? 'dark' : 'light');
    surChangement(themeActif());
  });
}

/** Bascule d'un thème à l'autre et mémorise le choix. */
export function basculerTheme(): Theme {
  const suivant: Theme = themeActif() === 'dark' ? 'light' : 'dark';
  poser(suivant);
  try {
    localStorage.setItem(CLE, suivant);
  } catch {
    // Le thème reste appliqué pour la session ; il ne survivra pas au rechargement.
  }
  return suivant;
}
